// エリア学習システムの型定義（API レスポンスのフロント側ミラー）
// 構造：エリア → セクション（文法1項目）→ ユニット（5/セクション）→ 問題
// 正の定義は Backend の content/lessons.ts・content/areas.ts。

export type AreaId =
  | "airport"
  | "home"
  | "school"
  | "shop"
  | "cafe"
  | "station"
  | "library"
  | "park"
  | "friendhouse"
  | "hospital"
  | "cityoffice"
  | "workplace";

export type Zone = "prologue" | "arrival" | "life";

export type LessonPhase = "recognition" | "comprehension" | "production" | "review";

// ── 問題（タスク）─────────────────────────────────────────────
export type VocabTask = {
  id: string;
  type: "vocab";
  word: string;
  reading?: string;
  answer: string;
  choices: string[];
};

export type TranslateTask = {
  id: string;
  type: "translate";
  sentence: string;
  answer: string;
  choices: string[];
};

export type WordOrderTask = {
  id: string;
  type: "word_order";
  prompt: string;
  tokens: string[];
  distractors: string[];
};

export type FillBlankTask = {
  id: string;
  type: "fill_blank";
  before: string;
  after: string;
  prompt?: string;
  answer: string;
  choices: string[];
};

export type Task = VocabTask | TranslateTask | WordOrderTask | FillBlankTask;

/** 文法解説（Skill Card）。セクション最初のユニットで表示。 */
export type LessonExplanation = {
  pattern: string;
  summary: string;
  examples: { ja: string; en: string }[];
  tip?: string;
};

/** 問題プール（段階別）。ユニットはここからランダム抽出される。 */
export type LessonPool = {
  recognition: Task[];
  comprehension: Task[];
  production: Task[];
  review: Task[];
};

// ── API レスポンス ───────────────────────────────────────────
/** GET /api/learner/areas の1要素 */
export type AreaSummary = {
  id: AreaId;
  name_ja: string;
  name_en: string;
  zone: Zone;
  level: string;
  theme_ja: string;
  theme_en: string;
  description_ja: string;
  description_en: string;
  unlock_order: number;
  unlock_condition?: { type: string; area: string } | null;
  unlocked: boolean;
  section_count: number;
  unit_total: number;
  unit_done: number;
  cleared: boolean;
};

/** GET /api/learner/areas/:id の sections 要素 */
export type SectionSummary = {
  id: string;
  index: number;
  title_ja: string;
  title_en: string;
  unit_total: number;
  unit_done: number;
  cleared: boolean;
};

/** GET /api/learner/areas/:id の units 要素（セクションを横断したフラットなユニット一覧） */
export type UnitSummary = {
  id: string;
  section_id: string;
  section_index: number;
  unit_index: number;
  section_title_ja: string;
  section_title_en: string;
  completed: boolean;
};

export type AreaDetail = {
  area: {
    id: AreaId;
    name_ja: string;
    name_en: string;
    zone: Zone;
    level: string;
    theme_ja: string;
    theme_en: string;
    description_ja: string;
    description_en: string;
    unlocked: boolean;
  };
  sections: SectionSummary[];
  units: UnitSummary[];
};

/** ユニットの出題プラン（段階別の出題数） */
export type UnitPlan = Partial<Record<LessonPhase, number>>;

export type UnitInfo = {
  index: number;
  id: string;
  plan: UnitPlan;
  completed: boolean;
};

/** GET /api/learner/sections/:id */
export type Section = {
  id: string;
  area_id: AreaId;
  index: number;
  title_ja: string;
  title_en: string;
  explanation?: LessonExplanation | null;
  pool: LessonPool;
  units: UnitInfo[];
};

/** POST /api/learner/units/:unitId/complete のレスポンス */
export type UnitResult = {
  unit_id: string;
  section_id: string;
  area_id: AreaId;
  unit_index: number;
  total: number;
  correct_count: number;
  accuracy: number;
  xp_awarded: number;
  coin_awarded: number;
  is_first_clear: boolean;
  section_done: number;
  section_total: number;
  section_cleared: boolean;
  section_just_cleared: boolean;
  area_just_cleared: boolean;
  newly_unlocked_areas: Array<{ id: AreaId; name_ja: string; name_en: string }>;
  results: Array<{ task_id: string; is_correct: boolean; correct_answer: string }>;
};

// ── ヘルパ ───────────────────────────────────────────────────
function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

const PHASES: LessonPhase[] = ["recognition", "comprehension", "production", "review"];
/** ユニット1回あたりの目安出題数 */
const UNIT_TARGET = 5;

/**
 * 指定ユニットの1セッション分の問題を、セクションのプールから抽出する。
 * ユニットの plan に従い段階順に抽出。短すぎる場合は他段階から補充する。
 * 毎回ランダムなので、再挑戦のたび違う問題になる。
 */
export function buildUnit(section: Section, unitIndex: number): Task[] {
  const unit = section.units.find((u) => u.index === unitIndex);
  if (!unit) return [];
  const pick = (arr: Task[], n: number): Task[] =>
    shuffle(arr).slice(0, Math.max(0, Math.min(n, arr.length)));
  const out: Task[] = [];
  for (const phase of PHASES) {
    out.push(...pick(section.pool[phase], unit.plan[phase] ?? 0));
  }
  // 短いユニットは産出→理解→認識→復習の順に補充して目安数に近づける
  if (out.length < UNIT_TARGET) {
    const used = new Set(out.map((t) => t.id));
    for (const phase of ["production", "comprehension", "recognition", "review"] as const) {
      for (const t of shuffle(section.pool[phase])) {
        if (out.length >= UNIT_TARGET) break;
        if (!used.has(t.id)) {
          out.push(t);
          used.add(t.id);
        }
      }
    }
  }
  return out;
}

/** タスクの正解（ローカル即時フィードバック用） */
export function localAnswer(task: Task): string {
  return task.type === "word_order" ? task.tokens.join(" ") : task.answer;
}
